import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import {
  auth,
  onAuthStateChanged,
  signInWithGoogle,
  signInWithApple,
  signInWithEmail,
  signUpWithEmail,
  resetPassword,
  logOut,
  dbGet,
  dbSet,
  dbUpdate,
  subscribeToPath,
  uploadFile,
  increment,
  FirebaseUser,
} from "../config/firebase";
import {
  User,
  GTWallet,
  SignupOffer,
  GTTransaction,
  SignupOfferRedemption,
} from "../types";

// Test mode prefix (duplicated here to avoid circular dependency)
// Using versioned paths: test-mode/v1 for test, v1 for production
const TEST_MODE_PREFIX = "test-mode/v1";
const PROD_PREFIX = "v1";

// Default founding member offer (used if no offers exist in database)
const DEFAULT_FOUNDING_OFFER: SignupOffer = {
  offerId: "founding-member",
  name: "Founding Member",
  description: "Be a founding member of ParlayGround",
  gtBonus: 100,
  tagId: "og", // OG tag for founding members
  badgeId: "founding-member",
  badgeImageUrl: "/assets/badges/founding-member-badge.png",
  discordLink: "https://discord.com/invite/kayaMSnjR",
  welcomeTitle: "Welcome, Founding Member!",
  welcomeMessage:
    "You're now part of an exclusive group shaping the future of sports fandom. As a founding member, you're not just a user - you're a community builder.",
  isActive: true,
  startsAt: Date.now(),
  expiresAt: new Date("2026-12-26T00:00:00Z").getTime(),
  maxRedemptions: null,
  currentRedemptions: 0,
  createdAt: Date.now(),
  updatedAt: Date.now(),
  createdBy: "system",
};

interface AuthContextType {
  user: User | null;
  firebaseUser: FirebaseUser | null;
  wallet: GTWallet | null;
  loading: boolean;
  error: string | null;
  isAdmin: boolean;
  isNewSignup: boolean;
  activeSignupOffer: SignupOffer | null;
  redeemedOffer: SignupOffer | null;
  needsUsername: boolean;
  pendingSocialUser: FirebaseUser | null;

  // Auth methods
  loginWithGoogle: () => Promise<void>;
  loginWithApple: () => Promise<void>;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  signupWithEmail: (
    email: string,
    password: string,
    username: string,
    photoFile?: File | null
  ) => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  clearNewSignupFlag: () => void;
  completeSocialSignup: (
    username: string,
    photoFile?: File | null
  ) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [wallet, setWallet] = useState<GTWallet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isNewSignup, setIsNewSignup] = useState(false);
  const [activeSignupOffer, setActiveSignupOffer] =
    useState<SignupOffer | null>(null);
  const [redeemedOffer, setRedeemedOffer] = useState<SignupOffer | null>(null);
  const [needsUsername, setNeedsUsername] = useState(false);
  const [pendingSocialUser, setPendingSocialUser] =
    useState<FirebaseUser | null>(null);
  const [pendingSocialProvider, setPendingSocialProvider] = useState<
    "google" | "apple" | null
  >(null);

  // Fetch active signup offers from database
  useEffect(() => {
    const fetchActiveOffer = async () => {
      try {
        const now = Date.now();

        // First check config/foundingMember (legacy/simple config)
        const configSnapshot = await dbGet(`${PROD_PREFIX}/config/app/foundingMember`);
        if (configSnapshot.exists()) {
          const config = configSnapshot.val();
          if (config.enabled && now < config.expiresAt) {
            // Convert simple config to SignupOffer format
            const offer: SignupOffer = {
              offerId: "founding-member",
              name: "Founding Member",
              description: "Be a founding member of ParlayGround",
              gtBonus: config.gtBonus || 100,
              tagId: config.tagId || "og", // OG tag for founding members
              badgeId: config.badgeId || "founding-member",
              badgeImageUrl: "/assets/badges/founding-member-badge.png",
              discordLink: config.discordLink || null,
              welcomeTitle: "Welcome, Founding Member!",
              welcomeMessage:
                "You're now part of an exclusive group shaping the future of sports fandom. As a founding member, you're not just a user - you're a community builder.",
              isActive: true,
              startsAt: 0,
              expiresAt: config.expiresAt,
              maxRedemptions: null,
              currentRedemptions: 0,
              createdAt: Date.now(),
              updatedAt: Date.now(),
              createdBy: "system",
            };
            setActiveSignupOffer(offer);
            return;
          }
        }

        // Then check signupOffers collection
        const snapshot = await dbGet(`${PROD_PREFIX}/config/signupOffers`);
        if (snapshot.exists()) {
          const offers = snapshot.val() as Record<string, SignupOffer>;

          // Find the first active offer that hasn't expired and hasn't hit max redemptions
          const activeOffer = Object.values(offers).find(
            (offer) =>
              offer.isActive &&
              now >= offer.startsAt &&
              now < offer.expiresAt &&
              (offer.maxRedemptions === null ||
                offer.currentRedemptions < offer.maxRedemptions)
          );

          setActiveSignupOffer(activeOffer || null);
        } else {
          // No offers exist anywhere, use default
          setActiveSignupOffer(DEFAULT_FOUNDING_OFFER);
        }
      } catch (err) {
        console.error("Error fetching signup offers:", err);
        setActiveSignupOffer(DEFAULT_FOUNDING_OFFER);
      }
    };
    fetchActiveOffer();

    // Subscribe to config/foundingMember changes
    const unsubConfig = subscribeToPath(`${PROD_PREFIX}/config/app/foundingMember`, (data) => {
      if (data) {
        const config = data as {
          enabled: boolean;
          expiresAt: number;
          gtBonus: number;
          badgeId: string;
          discordLink?: string;
          tagId?: string;
        };
        const now = Date.now();
        if (config.enabled && now < config.expiresAt) {
          const offer: SignupOffer = {
            offerId: "founding-member",
            name: "Founding Member",
            description: "Be a founding member of ParlayGround",
            gtBonus: config.gtBonus || 100,
            tagId: config.tagId || "og", // OG tag for founding members
            badgeId: config.badgeId || "founding-member",
            badgeImageUrl: "/assets/badges/founding-member-badge.png",
            discordLink: config.discordLink || null,
            welcomeTitle: "Welcome, Founding Member!",
            welcomeMessage:
              "You're now part of an exclusive group shaping the future of sports fandom. As a founding member, you're not just a user - you're a community builder.",
            isActive: true,
            startsAt: 0,
            expiresAt: config.expiresAt,
            maxRedemptions: null,
            currentRedemptions: 0,
            createdAt: Date.now(),
            updatedAt: Date.now(),
            createdBy: "system",
          };
          setActiveSignupOffer(offer);
        }
      }
    });

    // Subscribe to signupOffers changes
    const unsubOffers = subscribeToPath(`${PROD_PREFIX}/config/signupOffers`, (data) => {
      if (data) {
        const offers = data as Record<string, SignupOffer>;
        const now = Date.now();
        const activeOffer = Object.values(offers).find(
          (offer) =>
            offer.isActive &&
            now >= offer.startsAt &&
            now < offer.expiresAt &&
            (offer.maxRedemptions === null ||
              offer.currentRedemptions < offer.maxRedemptions)
        );
        // Only set if no config/foundingMember is active
        if (activeOffer) {
          setActiveSignupOffer(activeOffer);
        }
      }
    });

    return () => {
      unsubConfig();
      unsubOffers();
    };
  }, []);

  // Fetch user profile from database
  const fetchUserProfile = useCallback(
    async (uid: string): Promise<User | null> => {
      try {
        const snapshot = await dbGet(`${PROD_PREFIX}/users/profiles/${uid}`);
        if (snapshot.exists()) {
          return snapshot.val() as User;
        }
        return null;
      } catch (err) {
        console.error("Error fetching user profile:", err);
        return null;
      }
    },
    []
  );

  // Create new user profile
  const createUserProfile = useCallback(
    async (
      firebaseUser: FirebaseUser,
      provider: "email" | "google" | "apple",
      username?: string
    ): Promise<{ user: User; appliedOffer: SignupOffer | null }> => {
      const now = Date.now();

      // Check for active signup offer - fetch fresh from DB to avoid race conditions
      let offer = activeSignupOffer;

      if (!offer) {
        // Try to fetch offer directly from database
        try {
          // First check config/foundingMember
          const configSnapshot = await dbGet(`${PROD_PREFIX}/config/app/foundingMember`);
          if (configSnapshot.exists()) {
            const config = configSnapshot.val();
            if (config.enabled && now < config.expiresAt) {
              offer = {
                offerId: "founding-member",
                name: "Founding Member",
                description: "Be a founding member of ParlayGround",
                gtBonus: config.gtBonus || 100,
                tagId: config.tagId || "og", // OG tag for founding members
                badgeId: config.badgeId || "founding-member",
                badgeImageUrl: "/assets/badges/founding-member-badge.png",
                discordLink: config.discordLink || null,
                welcomeTitle: "Welcome, Founding Member!",
                welcomeMessage:
                  "You're now part of an exclusive group shaping the future of sports fandom. As a founding member, you're not just a user - you're a community builder.",
                isActive: true,
                startsAt: 0,
                expiresAt: config.expiresAt,
                maxRedemptions: null,
                currentRedemptions: 0,
                createdAt: Date.now(),
                updatedAt: Date.now(),
                createdBy: "system",
              };
            }
          }

          // Then check signupOffers collection if no config offer found
          if (!offer) {
            const snapshot = await dbGet(`${PROD_PREFIX}/config/signupOffers`);
            if (snapshot.exists()) {
              const offers = snapshot.val() as Record<string, SignupOffer>;
              offer =
                Object.values(offers).find(
                  (o) =>
                    o.isActive &&
                    now >= o.startsAt &&
                    now < o.expiresAt &&
                    (o.maxRedemptions === null ||
                      o.currentRedemptions < o.maxRedemptions)
                ) || null;
            } else {
              // Use default offer as fallback
              offer = DEFAULT_FOUNDING_OFFER;
            }
          }
        } catch (err) {
          console.error("Error fetching offer during signup:", err);
          offer = DEFAULT_FOUNDING_OFFER;
        }
      }

      // Determine GT bonus based on offer, capped to prevent abuse
      const MAX_SIGNUP_BONUS = 100;
      const gtBonus = Math.min(offer ? offer.gtBonus : 100, MAX_SIGNUP_BONUS);

      // Build badges object
      const badges: Record<string, boolean> = {};
      if (offer?.badgeId) {
        badges[offer.badgeId] = true;
      }

      const newUser: User = {
        uid: firebaseUser.uid,
        email: firebaseUser.email || "",
        username:
          username ||
          firebaseUser.displayName?.replace(/\s+/g, "") ||
          `user${now}`,
        displayName:
          firebaseUser.displayName || username || "ParlayGround User",
        photoURL:
          firebaseUser.photoURL ||
          `https://i.pravatar.cc/150?u=${firebaseUser.uid}`,
        bio: "",
        provider,
        isAdmin: false,
        isBanned: false,
        banReason: null,
        bannedAt: null,
        bannedBy: null,
        banExpiresAt: null,
        isFoundingMember: offer?.offerId === "founding-member",
        foundingMemberDate: offer?.offerId === "founding-member" ? now : null,
        settings: {
          notifications: {
            picks: true,
            sweatrooms: true,
            follows: true,
            tips: true,
            trivia: true,
          },
          darkMode: false,
          tutorialCompleted: false,
          privacy: {
            profilePublic: true,
            showActivity: true,
          },
        },
        reputation: {
          score: 0,
          level: 1,
          currentStreak: 0,
          bestStreak: 0,
          lastActiveDate: new Date().toISOString().split("T")[0],
          badges,
        },
        stats: {
          totalSlips: 0,
          wins: 0,
          losses: 0,
          pushes: 0,
          winRate: 0,
          totalTails: 0,
          sweatRoomsJoined: 0,
          tipsGiven: 0,
          tipsReceived: 0,
          triviaCorrect: 0,
        },
        createdAt: now,
        updatedAt: now,
      };

      // Save user to database
      await dbSet(`${PROD_PREFIX}/users/profiles/${firebaseUser.uid}`, newUser);

      // Initialize GT wallet
      const initialWallet: GTWallet = {
        balance: gtBonus,
        totalEarned: gtBonus,
        totalSpent: 0,
        totalTippedGiven: 0,
        totalTippedReceived: 0,
        lastUpdated: now,
        version: 1,
      };
      await dbSet(`${PROD_PREFIX}/wallet/balances/${firebaseUser.uid}`, initialWallet);

      // Reserve username
      await dbSet(`${PROD_PREFIX}/users/usernames/${newUser.username.toLowerCase()}`, {
        userId: firebaseUser.uid,
      });

      // If there's an active offer, log the transaction and redemption
      if (offer) {
        // Create GT transaction for the signup offer bonus
        const transactionId = `txn_${firebaseUser.uid}_${now}`;
        const transaction: GTTransaction = {
          transactionId,
          type: "signup_offer",
          amount: gtBonus,
          balanceBefore: 0,
          balanceAfter: gtBonus,
          referenceId: offer.offerId,
          referenceType: "signup_offer",
          description: `${offer.name} signup bonus`,
          metadata: {
            offerName: offer.name,
            offerId: offer.offerId,
            badgeAwarded: offer.badgeId,
          },
          createdAt: now,
          processedBy: "system",
        };
        await dbSet(
          `${PROD_PREFIX}/wallet/transactions/${firebaseUser.uid}/${transactionId}`,
          transaction
        );

        // Create redemption record
        const redemptionId = `redemp_${firebaseUser.uid}_${now}`;
        const redemption: SignupOfferRedemption = {
          redemptionId,
          offerId: offer.offerId,
          offerName: offer.name,
          userId: firebaseUser.uid,
          username: newUser.username,
          gtAwarded: gtBonus,
          tagAwarded: offer.tagId || null,
          badgeAwarded: offer.badgeId,
          redeemedAt: now,
        };
        await dbSet(`${PROD_PREFIX}/config/offerRedemptions/${redemptionId}`, redemption);

        // Add tag to user's tags array if one was awarded
        if (offer.tagId) {
          const userTags = newUser.tags || [];
          if (!userTags.includes(offer.tagId)) {
            await dbUpdate(`${PROD_PREFIX}/users/profiles/${firebaseUser.uid}`, {
              tags: [...userTags, offer.tagId],
            });
          }
        }

        // Atomically increment the offer's redemption count
        await dbUpdate(`${PROD_PREFIX}/config/signupOffers/${offer.offerId}`, {
          currentRedemptions: increment(1),
          updatedAt: now,
        });
      }

      // Always add "rookie" tag to all new users
      // Fetch current tags in case offer tag was just added
      const userSnapshot = await dbGet(`${PROD_PREFIX}/users/profiles/${firebaseUser.uid}`);
      const currentUserData = userSnapshot.exists()
        ? (userSnapshot.val() as User)
        : newUser;
      const updatedTags = currentUserData.tags || [];
      if (!updatedTags.includes("rookie")) {
        await dbUpdate(`${PROD_PREFIX}/users/profiles/${firebaseUser.uid}`, {
          tags: [...updatedTags, "rookie"],
        });
      }

      return { user: newUser, appliedOffer: offer };
    },
    [activeSignupOffer]
  );

  // Subscribe to user, wallet, and admin status changes
  // We need to track test mode here for wallet subscription since AuthContext wraps TestModeContext
  const [localTestMode, setLocalTestMode] = useState(() => {
    // Check localStorage on init
    return localStorage.getItem("pg_test_mode") === "true";
  });

  // Listen to localStorage changes (from TestModeContext toggle)
  useEffect(() => {
    const handleStorageChange = () => {
      const testModeEnabled = localStorage.getItem("pg_test_mode") === "true";
      setLocalTestMode(testModeEnabled);
    };

    // Listen for storage events (when changed from another tab/window)
    window.addEventListener("storage", handleStorageChange);

    // Also check on visibility change (for same-tab localStorage updates before page reload)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        handleStorageChange();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (!firebaseUser) {
      setUser(null);
      setWallet(null);
      setIsAdmin(false);
      return;
    }

    // Subscribe to user profile changes
    const unsubUser = subscribeToPath(`${PROD_PREFIX}/users/profiles/${firebaseUser.uid}`, (data) => {
      if (data) {
        setUser(data as User);
      }
    });

    // Subscribe to wallet changes - use versioned paths with test mode prefix if admin and test mode enabled
    const walletPath =
      localTestMode && isAdmin
        ? `${TEST_MODE_PREFIX}/wallet/balances/${firebaseUser.uid}`
        : `${PROD_PREFIX}/wallet/balances/${firebaseUser.uid}`;

    const unsubWallet = subscribeToPath(walletPath, (data) => {
      if (data) {
        setWallet(data as GTWallet);
      } else {
        // No wallet at this path - might be test mode with no test wallet yet
        setWallet(null);
      }
    });

    // Subscribe to admin status (checks v1/users/admins/{uid} path)
    const unsubAdmin = subscribeToPath(`${PROD_PREFIX}/users/admins/${firebaseUser.uid}`, (data) => {
      // User is admin if the path exists and has any truthy value
      setIsAdmin(!!data);
    });

    return () => {
      unsubUser();
      unsubWallet();
      unsubAdmin();
    };
  }, [firebaseUser, localTestMode, isAdmin]);

  // Auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);

      if (fbUser) {
        const profile = await fetchUserProfile(fbUser.uid);
        if (profile) {
          setUser(profile);

          // Check if user is banned
          if (profile.isBanned) {
            const banExpired =
              profile.banExpiresAt && profile.banExpiresAt < Date.now();
            if (!banExpired) {
              setError(
                `Your account has been banned. Reason: ${
                  profile.banReason || "Violation of terms"
                }`
              );
              await logOut();
              setFirebaseUser(null);
              setUser(null);
            } else {
              // Ban expired, remove it
              await dbUpdate(`${PROD_PREFIX}/users/profiles/${fbUser.uid}`, {
                isBanned: false,
                banReason: null,
                bannedAt: null,
                bannedBy: null,
                banExpiresAt: null,
              });
            }
          }
        }
      } else {
        setUser(null);
        setWallet(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [fetchUserProfile]);

  // Login with Google
  const loginWithGoogle = async () => {
    setError(null);
    setLoading(true);
    try {
      const result = await signInWithGoogle();
      const profile = await fetchUserProfile(result.user.uid);
      if (!profile) {
        // New user - needs to set username
        setPendingSocialUser(result.user);
        setPendingSocialProvider("google");
        setNeedsUsername(true);
      }
    } catch (err: any) {
      console.error("Google login error:", err);
      setError(err.message || "Failed to sign in with Google");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Login with Apple
  const loginWithApple = async () => {
    setError(null);
    setLoading(true);
    try {
      const result = await signInWithApple();
      const profile = await fetchUserProfile(result.user.uid);
      if (!profile) {
        // New user - needs to set username
        setPendingSocialUser(result.user);
        setPendingSocialProvider("apple");
        setNeedsUsername(true);
      }
    } catch (err: any) {
      console.error("Apple login error:", err);
      setError(err.message || "Failed to sign in with Apple");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Login with Email
  const loginWithEmailFn = async (email: string, password: string) => {
    setError(null);
    setLoading(true);
    try {
      await signInWithEmail(email, password);
    } catch (err: any) {
      console.error("Email login error:", err);
      const errorMessage = getAuthErrorMessage(err.code);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Signup with Email
  const signupWithEmailFn = async (
    email: string,
    password: string,
    username: string,
    photoFile?: File | null
  ) => {
    setError(null);
    setLoading(true);
    try {
      // Check if username is available
      const usernameSnapshot = await dbGet(
        `${PROD_PREFIX}/users/usernames/${username.toLowerCase()}`
      );
      if (usernameSnapshot.exists()) {
        throw new Error("Username is already taken");
      }

      const result = await signUpWithEmail(email, password);
      const { appliedOffer } = await createUserProfile(
        result.user,
        "email",
        username
      );

      // Upload custom photo if provided
      if (photoFile) {
        const ext = photoFile.name.split(".").pop();
        const storagePath = `profile-pictures/${result.user.uid}/avatar.${ext}`;
        const downloadUrl = await uploadFile(storagePath, photoFile);
        await dbUpdate(`${PROD_PREFIX}/users/profiles/${result.user.uid}`, {
          photoURL: downloadUrl,
          updatedAt: Date.now(),
        });
      }

      if (appliedOffer) {
        setIsNewSignup(true);
        setRedeemedOffer(appliedOffer);
      }
    } catch (err: any) {
      console.error("Email signup error:", err);
      const errorMessage = err.message || getAuthErrorMessage(err.code);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Send Password Reset
  const sendPasswordResetFn = async (email: string) => {
    setError(null);
    try {
      await resetPassword(email);
    } catch (err: any) {
      console.error("Password reset error:", err);
      const errorMessage = getAuthErrorMessage(err.code);
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Logout
  const logout = async () => {
    setError(null);
    try {
      await logOut();
      setUser(null);
      setWallet(null);
    } catch (err: any) {
      console.error("Logout error:", err);
      setError(err.message || "Failed to log out");
      throw err;
    }
  };

  // Clear error
  const clearError = () => setError(null);

  // Clear new signup flag (after showing the welcome modal)
  const clearNewSignupFlag = () => {
    setIsNewSignup(false);
    setRedeemedOffer(null);
  };

  // Complete social signup by setting username and optional photo
  const completeSocialSignup = async (
    username: string,
    photoFile?: File | null
  ) => {
    if (!pendingSocialUser || !pendingSocialProvider) {
      throw new Error("No pending social signup");
    }

    setError(null);
    setLoading(true);

    try {
      // Check if username is available
      const usernameSnapshot = await dbGet(
        `${PROD_PREFIX}/users/usernames/${username.toLowerCase()}`
      );
      if (usernameSnapshot.exists()) {
        throw new Error("Username is already taken");
      }

      // Create the user profile with the chosen username
      const { appliedOffer } = await createUserProfile(
        pendingSocialUser,
        pendingSocialProvider,
        username
      );

      // Upload custom photo if provided
      if (photoFile) {
        const ext = photoFile.name.split(".").pop();
        const storagePath = `profile-pictures/${pendingSocialUser.uid}/avatar.${ext}`;
        const downloadUrl = await uploadFile(storagePath, photoFile);
        await dbUpdate(`${PROD_PREFIX}/users/profiles/${pendingSocialUser.uid}`, {
          photoURL: downloadUrl,
          updatedAt: Date.now(),
        });
      }

      // Clear pending state
      setPendingSocialUser(null);
      setPendingSocialProvider(null);
      setNeedsUsername(false);

      // Set new signup flags if offer was applied
      if (appliedOffer) {
        setIsNewSignup(true);
        setRedeemedOffer(appliedOffer);
      }
    } catch (err: any) {
      console.error("Complete social signup error:", err);
      setError(err.message || "Failed to complete signup");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    firebaseUser,
    wallet,
    loading,
    error,
    isAdmin,
    isNewSignup,
    activeSignupOffer,
    redeemedOffer,
    needsUsername,
    pendingSocialUser,
    loginWithGoogle,
    loginWithApple,
    loginWithEmail: loginWithEmailFn,
    signupWithEmail: signupWithEmailFn,
    sendPasswordReset: sendPasswordResetFn,
    logout,
    clearError,
    clearNewSignupFlag,
    completeSocialSignup,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Helper function to get user-friendly error messages
function getAuthErrorMessage(code: string): string {
  switch (code) {
    case "auth/email-already-in-use":
      return "This email is already registered. Try logging in instead.";
    case "auth/invalid-email":
      return "Please enter a valid email address.";
    case "auth/operation-not-allowed":
      return "This sign-in method is not enabled.";
    case "auth/weak-password":
      return "Password should be at least 6 characters.";
    case "auth/user-disabled":
      return "This account has been disabled.";
    case "auth/user-not-found":
      return "No account found with this email.";
    case "auth/wrong-password":
      return "Incorrect password. Please try again.";
    case "auth/invalid-credential":
      return "Invalid email or password.";
    case "auth/too-many-requests":
      return "Too many attempts. Please try again later.";
    case "auth/popup-closed-by-user":
      return "Sign-in popup was closed. Please try again.";
    default:
      return "An error occurred. Please try again.";
  }
}
